import React, { useState } from "react";
import {
  Alert,
  Avatar,
  Box,
  Button,
  Card,
  CardContent,
  Chip,
  Grid,
  IconButton,
  InputAdornment,
  LinearProgress,
  ListItemIcon,
  Menu,
  MenuItem,
  Paper,
  Stack,
  TextField,
  ToggleButton,
  ToggleButtonGroup,
  Typography,
} from "@mui/material";
import {
  formatDateMMMddYYYY,
  getErrorResponse,
  toastError,
} from "../../utils/util";
import RdMyModal from "../../widgets/myModal/RdMyModal";
import AddSupplierItem from "./AddSupplierItem";
import AddIcon from "@mui/icons-material/Add";
import DeleteIcon from "@mui/icons-material/Delete";
import MoreVertIcon from "@mui/icons-material/MoreVert";
import EditIcon from "@mui/icons-material/Edit";
import GridViewIcon from "@mui/icons-material/GridView";
import ViewListIcon from "@mui/icons-material/ViewList";
import SearchIcon from "@mui/icons-material/Search";
import StorefrontIcon from "@mui/icons-material/Storefront";
import CalendarTodayIcon from "@mui/icons-material/CalendarToday";

import { API_ENDPOINT } from "../../utils/constants";
import SimpleYesNoDialog from "../../widgets/SimpleYesNoDialog";
import CustomCircularP from "../../widgets/CustomCircularP";
import axios from "axios";
import { toast } from "react-toastify";
import useApiEp from "../../hooks/useApiEp";
import { getHeader } from "../../utils/utils2";

const SupplierItems = () => {
  const [reloadItems, setReloadItems] = useState(false);
  const [showAddSupplier, setShowAddSupplier] = useState(false);
  const [viewMode, setViewMode] = useState("grid"); // 'grid' or 'list'
  const [searchQuery, setSearchQuery] = useState("");

  const {
    data: formItems,
    isLoading,
    isError,
    error,
  } = useApiEp(`/admin/get-suppliers?reload=${reloadItems}`);
  const [actionMode, setActionMode] = useState("add");
  const [showDeleteItem, setShowDeleteItem] = useState(false);
  const [deletingItem, setDeletingItem] = useState(false);
  const [currentItem, setCurrentItem] = useState(null);
  const [anchorEl, setAnchorEl] = useState(null);
  const [selectedItem, setSelectedItem] = useState(null);

  const open = Boolean(anchorEl);

  if (isLoading) {
    return <LinearProgress sx={{ my: 2 }} />;
  }
  if (isError) {
    return <Alert severity="error" sx={{ my: 2 }}>{getErrorResponse(error)}</Alert>;
  }

  const handleMenuClick = (event, item) => {
    setAnchorEl(event.currentTarget);
    setSelectedItem(item);
  };

  const handleMenuClose = () => {
    setAnchorEl(null);
  };

  const handleEditItem = (item) => {
    setActionMode("edit");
    setCurrentItem(item);
    handleMenuClose();
    setShowAddSupplier((prev) => !prev);
  };

  const handleDeleteItem = (item) => {
    setCurrentItem(item);
    handleMenuClose();
    setShowDeleteItem((prev) => !prev);
  };

  const handleAddItem = () => {
    setActionMode("add");
    setShowAddSupplier((prev) => !prev);
  };

  const onAddedItem = () => {
    setShowAddSupplier((prev) => !prev);
    setReloadItems((prev) => !prev);
  };

  const onYesDelete = async () => {
    setShowDeleteItem((prev) => !prev);
    if (!currentItem) return;
    try {
      setDeletingItem(true);
      const { data } = await axios.delete(
        `${API_ENDPOINT}/admin/delete-supplier?id=${currentItem._id}`,
        getHeader()
      );
      toast.success(data.message);
      setReloadItems((prev) => !prev);
    } catch (err) {
      toastError(err);
    } finally {
      setDeletingItem(false);
    }
  };

  const filteredItems = (formItems || []).filter((item) =>
    item.supplierName?.toLowerCase().includes(searchQuery.toLowerCase())
  );

  return (
    <Box>
      <CustomCircularP show={deletingItem} />
      <SimpleYesNoDialog
        title="Please confirm!"
        message="Are you sure you want to delete this supplier?"
        show={showDeleteItem}
        setShow={setShowDeleteItem}
        onYes={onYesDelete}
      />
      <RdMyModal
        mWidth={"720px"}
        showModal={showAddSupplier}
        setShowModal={setShowAddSupplier}
        modalC={
          <AddSupplierItem
            onAdded={onAddedItem}
            show={showAddSupplier}
            mode={actionMode}
            editItem={currentItem}
          />
        }
      />

      {/* Toolbar: Search, View Switcher, Add Supplier */}
      <Stack
        direction={{ xs: "column", sm: "row" }}
        spacing={2}
        alignItems={{ xs: "stretch", sm: "center" }}
        justifyContent="space-between"
        sx={{ mb: 3 }}
      >
        <TextField
          size="small"
          placeholder="Search suppliers by name..."
          value={searchQuery}
          onChange={(e) => setSearchQuery(e.target.value)}
          InputProps={{
            startAdornment: (
              <InputAdornment position="start">
                <SearchIcon color="action" fontSize="small" />
              </InputAdornment>
            ),
          }}
          sx={{
            minWidth: 260,
            flex: { sm: "0 1 320px" },
            "& .MuiOutlinedInput-root": {
              borderRadius: "10px",
              bgcolor: "#FFFFFF",
            },
          }}
        />

        <Stack direction="row" spacing={1.5} alignItems="center">
          <ToggleButtonGroup
            value={viewMode}
            exclusive
            onChange={(e, val) => val && setViewMode(val)}
            size="small"
            sx={{ bgcolor: "#FFFFFF", borderRadius: "10px" }}
          >
            <ToggleButton value="grid" aria-label="grid view">
              <GridViewIcon fontSize="small" />
            </ToggleButton>
            <ToggleButton value="list" aria-label="list view">
              <ViewListIcon fontSize="small" />
            </ToggleButton>
          </ToggleButtonGroup>

          <Button
            onClick={handleAddItem}
            size="medium"
            startIcon={<AddIcon />}
            variant="contained"
            disableElevation
            sx={{
              borderRadius: "10px",
              textTransform: "none",
              fontWeight: 600,
              px: 2.5,
              bgcolor: "#2563EB",
              "&:hover": { bgcolor: "#1D4ED8" },
            }}
          >
            Add Supplier
          </Button>
        </Stack>
      </Stack>

      {filteredItems.length === 0 && (
        <Paper
          elevation={0}
          sx={{
            p: 5,
            textAlign: "center",
            borderRadius: "14px",
            bgcolor: "#F8FAFC",
            border: "1px dashed #CBD5E1",
          }}
        >
          <Typography variant="h6" color="text.secondary" fontWeight={600}>
            {searchQuery ? "No matching suppliers found" : "No suppliers added yet"}
          </Typography>
          <Typography variant="body2" color="text.secondary" sx={{ mt: 0.5 }}>
            {searchQuery ? "Try searching with a different term" : "Click 'Add Supplier' above to create your first supplier profile."}
          </Typography>
        </Paper>
      )}

      {/* Grid View */}
      {viewMode === "grid" && filteredItems.length > 0 && (
        <Grid container spacing={2.5}>
          {filteredItems.map((item, index) => (
            <Grid item xs={12} sm={6} md={4} key={item._id || index}>
              <Card
                elevation={0}
                sx={{
                  height: "100%",
                  display: "flex",
                  flexDirection: "column",
                  justifyContent: "space-between",
                  borderRadius: "14px",
                  border: "1px solid #E2E8F0",
                  bgcolor: "#FFFFFF",
                  position: "relative",
                  overflow: "hidden",
                  boxShadow: "0px 2px 8px rgba(15, 23, 42, 0.04)",
                  transition: "transform 0.2s, box-shadow 0.2s",
                  "&:hover": {
                    transform: "translateY(-3px)",
                    boxShadow: "0px 10px 25px rgba(15, 23, 42, 0.08)",
                    borderColor: "#93C5FD",
                  },
                }}
              >
                {/* Top Status Accent Bar */}
                <Box sx={{ height: 4, width: "100%", bgcolor: "#2563EB" }} />

                <CardContent sx={{ p: 2.5 }}>
                  <Stack direction="row" justifyContent="space-between" alignItems="flex-start" mb={1.5}>
                    <Avatar
                      sx={{
                        bgcolor: "#EFF6FF",
                        color: "#2563EB",
                        fontWeight: 700,
                        width: 42,
                        height: 42,
                        border: "1px solid #BFDBFE",
                      }}
                    >
                      <StorefrontIcon fontSize="small" />
                    </Avatar>

                    <IconButton
                      size="small"
                      onClick={(e) => handleMenuClick(e, item)}
                      sx={{ color: "#64748B" }}
                    >
                      <MoreVertIcon fontSize="small" />
                    </IconButton>
                  </Stack>

                  <Typography
                    variant="h6"
                    sx={{
                      fontWeight: 700,
                      fontSize: "1.05rem",
                      color: "#0F172A",
                      mb: 1,
                      lineHeight: 1.3,
                    }}
                  >
                    {item.supplierName}
                  </Typography>

                  {item.createdOn && (
                    <Chip
                      icon={<CalendarTodayIcon style={{ fontSize: 13 }} />}
                      label={`Added: ${formatDateMMMddYYYY(item.createdOn)}`}
                      size="small"
                      sx={{
                        bgcolor: "#F1F5F9",
                        color: "#475467",
                        fontSize: "0.75rem",
                        fontWeight: 500,
                        mt: 0.5,
                      }}
                    />
                  )}
                </CardContent>

                {/* Bottom Action Footer */}
                <Box sx={{ p: 2, pt: 0 }}>
                  <Button
                    onClick={() => handleEditItem(item)}
                    fullWidth
                    size="small"
                    variant="outlined"
                    startIcon={<EditIcon fontSize="small" />}
                    sx={{
                      borderRadius: "8px",
                      textTransform: "none",
                      fontWeight: 600,
                      borderColor: "#E2E8F0",
                      color: "#2563EB",
                      bgcolor: "#EFF6FF",
                      py: 0.8,
                      "&:hover": {
                        borderColor: "#93C5FD",
                        bgcolor: "#DBEAFE",
                      },
                    }}
                  >
                    Edit Supplier Details
                  </Button>
                </Box>
              </Card>
            </Grid>
          ))}
        </Grid>
      )}

      {/* List View */}
      {viewMode === "list" && filteredItems.length > 0 && (
        <Stack spacing={1.5}>
          {filteredItems.map((item, index) => (
            <Card
              key={item._id || index}
              elevation={0}
              sx={{
                bgcolor: "#FFFFFF",
                borderRadius: "12px",
                border: "1px solid #E2E8F0",
                display: "flex",
                justifyContent: "space-between",
                alignItems: "center",
                p: 2,
                transition: "box-shadow 0.2s",
                "&:hover": { boxShadow: "0px 4px 12px rgba(15, 23, 42, 0.05)" },
              }}
            >
              <Stack direction="row" spacing={2} alignItems="center">
                <Avatar
                  sx={{
                    bgcolor: "#EFF6FF",
                    color: "#2563EB",
                    width: 40,
                    height: 40,
                  }}
                >
                  <StorefrontIcon fontSize="small" />
                </Avatar>
                <Box>
                  <Typography variant="subtitle1" sx={{ fontWeight: 700, color: "#0F172A" }}>
                    {item.supplierName}
                  </Typography>
                  <Typography variant="caption" color="textSecondary">
                    Added: {formatDateMMMddYYYY(item.createdOn)}
                  </Typography>
                </Box>
              </Stack>

              <Stack direction="row" spacing={1} alignItems="center">
                <Button
                  size="small"
                  variant="text"
                  onClick={() => handleEditItem(item)}
                  sx={{ textTransform: "none", fontWeight: 600, color: "#2563EB" }}
                >
                  Edit
                </Button>
                <IconButton onClick={(e) => handleMenuClick(e, item)}>
                  <MoreVertIcon />
                </IconButton>
              </Stack>
            </Card>
          ))}
        </Stack>
      )}

      {/* Shared Action Menu */}
      <Menu
        anchorEl={anchorEl}
        open={open}
        onClose={handleMenuClose}
        PaperProps={{
          elevation: 2,
          sx: { borderRadius: "10px", minWidth: 150, border: "1px solid #E2E8F0" },
        }}
      >
        <MenuItem onClick={() => selectedItem && handleEditItem(selectedItem)}>
          <ListItemIcon>
            <EditIcon fontSize="small" />
          </ListItemIcon>
          Edit Supplier
        </MenuItem>
        <MenuItem onClick={() => selectedItem && handleDeleteItem(selectedItem)} sx={{ color: "#DC2626" }}>
          <ListItemIcon>
            <DeleteIcon fontSize="small" sx={{ color: "#DC2626" }} />
          </ListItemIcon>
          Delete Supplier
        </MenuItem>
      </Menu>
    </Box>
  );
};

export default SupplierItems;
